import { useLang } from 'hooks/lang';

import { actions, CreateInvoiceEntryConfig } from 'entities';

import { Button, ResponsiveOverlay, Column } from 'components/Blocks';
import { CreateFileIcon } from 'components/Icons';

import { InvoiceBillingForm } from './Billing';
import { InvoiceValue } from './Value';

import { useFormControl, useWatch } from 'hooks';

import { invoiceCountriesConfig } from 'utils/countries';
import { extractFixedInvoiceData, validateCanCreate } from './utils';

import { InvoiceCreationProps, BaseInvoiceProps } from './types';

interface CreateInvoiceModalProps<BaseEntity extends BaseInvoiceProps> {
  children?: never;
  close: () => void;
  onCreateSuccess?: () => void;
  entities: BaseEntity[];
  allowBalances?: boolean;

  payloadGenerator(state?: InvoiceCreationProps): CreateInvoiceEntryConfig;
  creationPath?: string;
}

export function CreateInvoiceModal<BaseEntity extends BaseInvoiceProps>({
  close,
  onCreateSuccess,
  payloadGenerator,
  entities,
  allowBalances = false,
}: CreateInvoiceModalProps<BaseEntity>): JSX.Element {
  const { t } = useLang();

  const { client, country, clientName } = extractFixedInvoiceData(entities);

  const {
    percentageConfig: { required, name, value },
  } = invoiceCountriesConfig[country];

  const baseValues: InvoiceCreationProps = {
    emails: [] as string[],
    taxId: '',
    poNumber: '',
    clientPM: '',
    comment: '',
    address: '',
    companyName: '',
    description: '',

    dateRequest: {
      type: 'NOT_SPECIFIED',
    },

    clientBalances: [],

    additionalPercentage: required
      ? {
          name: name ?? '',
          value: value ?? '',
        }
      : undefined,
  };

  const { dispatch, state, handleChange } = useFormControl<InvoiceCreationProps>(baseValues);

  useWatch(entities, () => dispatch(baseValues));

  const canCreate = validateCanCreate(state);

  const createInvoice = actions.invoice.createInvoice.use({
    block: !canCreate,

    params: {
      ...state,

      clientBalances: state.clientBalances.map((balance) => balance.id),

      ...payloadGenerator(state),
    },

    onSuccess: () => {
      onCreateSuccess?.();
      close();
    },
  });

  return (
    <ResponsiveOverlay
      title={t('create-invoice') as string}
      allowEscape={false}
      // otherwise the event was triggering a delete
      close={() => close()}
      isOpen={!!entities.length}
      width="min(1080px, 90vw)"
      className="max-w-full overflow-y-auto"
      preventOpenAutoFocus
    >
      <Column className="phone:pb-4">
        <InvoiceBillingForm
          clientName={clientName}
          state={state}
          dispatch={dispatch}
          handleChange={handleChange}
          client={client}
        />

        <InvoiceValue
          entities={entities}
          state={state}
          dispatch={dispatch}
          allowBalances={allowBalances}
        />

        <Button
          {...createInvoice.buttonProps}
          disabled={!canCreate}
          className="ml-auto min-w-52 phone:w-full"
        >
          <CreateFileIcon size={18} />
          {t('create')}
        </Button>
      </Column>
    </ResponsiveOverlay>
  );
}
